/** Per-order limit when set (> 0). 0 or missing means unlimited. */
export function parseMaxOrderQty(
  maxOrderQty: number | null | undefined
): number | undefined {
  return typeof maxOrderQty === "number" &&
    Number.isFinite(maxOrderQty) &&
    maxOrderQty > 0
    ? maxOrderQty
    : undefined;
}

/**
 * Product-level quantity cap: min(stock, per-order limit).
 * Returns undefined when neither stock nor per-order limit is known.
 */
export function resolveProductQuantityCap(
  maxAvailable: number | null | undefined,
  maxOrderQty: number | null | undefined
): number | undefined {
  const stock =
    typeof maxAvailable === "number" && Number.isFinite(maxAvailable)
      ? maxAvailable
      : undefined;
  const perOrder = parseMaxOrderQty(maxOrderQty);

  if (stock !== undefined && perOrder !== undefined) {
    return Math.min(stock, perOrder);
  }
  if (perOrder !== undefined) return perOrder;
  if (stock !== undefined) return stock;
  return undefined;
}

/** Non-negative number for "Saadaval" labels and ticket pickers. */
export function resolveDisplayAvailable(
  maxAvailable: number | null | undefined,
  maxOrderQty: number | null | undefined
): number {
  return Math.max(0, resolveProductQuantityCap(maxAvailable, maxOrderQty) ?? 0);
}

/** Minimum selectable qty; defaults to 1. Enforced floor when minOrderQty > 1. */
export function resolveMinOrderQty(
  minOrderQty: number | null | undefined
): number {
  return typeof minOrderQty === "number" &&
    Number.isFinite(minOrderQty) &&
    minOrderQty > 1
    ? minOrderQty
    : 1;
}

export function hasMinOrderQtyConstraint(
  minOrderQty: number | null | undefined
): boolean {
  return (
    typeof minOrderQty === "number" &&
    Number.isFinite(minOrderQty) &&
    minOrderQty > 1
  );
}

/** Bump values in (0, min) up to min. */
export function clampQuantityToMinOrder(
  qty: number,
  minOrderQty: number | null | undefined
): number {
  const min = resolveMinOrderQty(minOrderQty);
  if (qty > 0 && min > 1 && qty < min) {
    return min;
  }
  return qty;
}

/**
 * Apply min-order floor, then max cap. Order matters when min > cap:
 * result is always <= cap when cap is defined.
 */
export function clampQuantityToMinAndCap(
  qty: number,
  minOrderQty: number | null | undefined,
  cap: number | null | undefined
): number {
  if (qty <= 0) {
    return 0;
  }
  let result = clampQuantityToMinOrder(qty, minOrderQty);
  if (
    cap !== undefined &&
    cap !== null &&
    typeof cap === "number" &&
    Number.isFinite(cap)
  ) {
    result = Math.min(result, cap);
  }
  return result;
}

/** First add from zero: min(minOrderQty, effectiveMax) when constrained, else 1. */
export function resolveStartQuantity(
  minOrderQty: number | null | undefined,
  effectiveMax: number
): number {
  if (hasMinOrderQtyConstraint(minOrderQty)) {
    return Math.min(resolveMinOrderQty(minOrderQty), effectiveMax);
  }
  return Math.min(1, effectiveMax);
}

export function isBelowMinOrderQty(
  qty: number,
  minOrderQty: number | null | undefined
): boolean {
  if (qty <= 0) return false;
  const min = resolveMinOrderQty(minOrderQty);
  return min > 1 && qty < min;
}

/** True when stock/cap cannot satisfy min order (or cap is zero). */
export function isEffectivelySoldOut(
  minOrderQty: number | null | undefined,
  effectiveMax: number
): boolean {
  if (effectiveMax <= 0) return true;
  if (!hasMinOrderQtyConstraint(minOrderQty)) return false;
  return effectiveMax < resolveMinOrderQty(minOrderQty);
}

/** Saadaval label: 0 when min order exceeds cap (avoids showing e.g. 3 + sold out). */
export function resolvePickerDisplayAvailable(
  minOrderQty: number | null | undefined,
  effectiveMax: number,
  displayValue: number = effectiveMax
): number {
  if (
    hasMinOrderQtyConstraint(minOrderQty) &&
    effectiveMax > 0 &&
    effectiveMax < resolveMinOrderQty(minOrderQty)
  ) {
    return 0;
  }
  return displayValue;
}
